import type ForceGraph from 'force-graph';
import type { GraphNode } from '../worker/api.js';

/** Internal node/link shapes shared by the graph modules. Node objects are
 * long-lived: they carry d3 simulation state (x/y/vx/vy) across data
 * refreshes so the layout never restarts from scratch. */

export type NodeKind = 'note' | 'attachment' | 'phantom';

export interface GNode extends GraphNode {
  // d3-force simulation state (mutated by force-graph).
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  fx?: number;
  fy?: number;
  index?: number;
  /** Derived render kind (phantom = unresolved link target). */
  kind: NodeKind;
  /** Precomputed draw radius (graph units). */
  radius: number;
  /** First matching color-group color, if any. */
  gcolor?: string;
}

export interface GLink {
  /** String id before force-graph ingests the data; node object after. */
  source: string | GNode;
  target: string | GNode;
}

export type FG = ForceGraph<GNode, GLink>;

/** Endpoint id of a link whether or not force-graph replaced it by the node. */
export const endId = (v: string | number | GNode | undefined): string =>
  typeof v === 'object' && v !== null ? String(v.id) : String(v ?? '');

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

/** Hex theme color (#rgb/#rgba/#rrggbb/#rrggbbaa) -> rgba() with alpha
 * multiplied in. Non-hex strings pass through unchanged. */
export function withAlpha(color: string, alpha: number): string {
  const m = /^#([0-9a-f]{3,8})$/i.exec(color.trim());
  if (!m) return color;
  let hex = m[1];
  if (hex.length === 3 || hex.length === 4) hex = [...hex].map((c) => c + c).join('');
  if (hex.length !== 6 && hex.length !== 8) return color;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  let a = clamp(alpha, 0, 1);
  if (hex.length === 8) a *= parseInt(hex.slice(6, 8), 16) / 255;
  return `rgba(${r},${g},${b},${Math.round(a * 1000) / 1000})`;
}

export interface ThemeColors {
  accent: string;
  accentStrong: string;
  muted: string;
  faint: string;
  /** Node label color. */
  text: string;
  /** Base link line color (alpha applied at draw time). */
  line: string;
  fontUi: string;
}

/** Read the CSS variables the canvas needs. Called at mount and whenever the
 * prefers-color-scheme media query flips. */
export function readThemeColors(): ThemeColors {
  const cs = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string): string =>
    cs.getPropertyValue(name).trim() || fallback;
  return {
    accent: v('--accent', '#8b7cf6'),
    accentStrong: v('--accent-strong', '#a495ff'),
    muted: v('--text-muted', '#9399b2'),
    faint: v('--text-faint', '#6c7086'),
    text: v('--text-muted', '#9399b2'),
    line: v('--text-faint', '#6c7086'),
    fontUi: v('--font-ui', 'sans-serif'),
  };
}

/** Obsidian-ish dot sizing: small base + sqrt(degree) growth, capped. */
export function nodeRadius(degree: number, kind: NodeKind): number {
  const base = kind === 'phantom' ? 1.8 : 2.5;
  return Math.min(base + Math.sqrt(Math.max(degree, 0)) * 0.9, 12);
}
